import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Users } from 'lucide-react'
import { casesAPI, doctorsAPI, favoritesAPI } from '../../services/api'
import { useProfileStore } from '../../stores'
import { getZipDistance } from '../../utils/zipDistance'
import { isDoctorInNetwork } from '../../utils/insuranceMatch'
import { useI18n } from '../../i18n'
import { DoctorCard } from '../../components/doctors/DoctorSearchKit'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner
import { PcpRow } from '../../components/doctors/DoctorsHomeKit' // s123 board D: the PCP (need not be on Orella)
import StartCaseSheet from './StartCaseSheet'
import { formatDate } from '../../utils/dateUtils'

/**
 * MyDoctorsScreen (v230.9.0)
 *
 * Dedicated destination for the patient's favorited + previously-seen doctors,
 * opened from the "My Doctors" button on DoctorsScreen. Grouped by specialty by
 * default; filterable by Favorites / Recently seen / In-network. Reuses the
 * redesigned DoctorCard (and its lazy next-slot field).
 */
export default function MyDoctorsScreen() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const insurance = useProfileStore((s) => s.profile?.insurance)
  const patientZip = useProfileStore((s) => s.profile?.identification?.zipCode || '')

  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all | favorites | seen | innetwork
  const [startOffice, setStartOffice] = useState(null) // s123: Send a case to the PCP (an office, not on Orella)
  const pcp = insurance && insurance.pcp && insurance.pcp.name ? insurance.pcp : null

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [casesResult, listResult, favs] = await Promise.all([
          casesAPI.list().catch(() => []),
          doctorsAPI.list().catch(() => []),
          favoritesAPI.getDoctors().catch(() => []),
        ])
        const caseList = Array.isArray(casesResult) ? casesResult : (casesResult?.cases || [])
        const seenIds = [...new Set(caseList.map(c => c.doctor_id).filter(Boolean))]
        // s123 (board D): the date of the latest case with each doctor ("Last case Oct 12").
        const lastCase = {}
        for (const c of caseList) {
          const when = c && c.doctor_id && (c.updated_at || c.created_at)
          if (when && (!lastCase[c.doctor_id] || String(when) > String(lastCase[c.doctor_id]))) lastCase[c.doctor_id] = when
        }
        const favIds = favs || []
        const wantedIds = [...new Set([...seenIds, ...favIds])]
        const all = Array.isArray(listResult) ? listResult : []
        const byId = new Map(all.map(d => [d.id, d]))
        const missing = wantedIds.filter(id => !byId.has(id))
        if (missing.length) {
          const fetched = await Promise.all(missing.map(id => doctorsAPI.get(id).catch(() => null)))
          fetched.filter(Boolean).forEach(d => byId.set(d.id, d))
        }
        const favSet = new Set(favIds)
        const seenSet = new Set(seenIds)
        const unified = wantedIds.map(id => byId.get(id)).filter(Boolean).map(d => ({
          ...d,
          _isFavorite: favSet.has(d.id),
          _isSeen: seenSet.has(d.id),
          _lastCase: lastCase[d.id] || null,
          _distance: (patientZip && d.practice_zip) ? getZipDistance(patientZip, d.practice_zip) : (d._distance ?? null),
        }))
        if (!cancelled) setDocs(unified)
      } catch (e) {
        console.error('[MyDoctors] load error', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [patientZip])

  // Lazily resolve next-available slots so cards show availability (pilot scale).
  useEffect(() => {
    const pending = docs.filter(d => d.availability_enabled === true && d._nextSlot === undefined)
    if (!pending.length) return
    let cancelled = false
    ;(async () => {
      const results = await Promise.all(pending.slice(0, 30).map(async (d) => {
        try {
          const r = await doctorsAPI.getAvailableSlots(d.id, 14)
          const slot = (r?.enabled && r?.slots?.length) ? r.slots[0] : null
          return [d.id, slot]
        } catch { return [d.id, null] }
      }))
      if (cancelled) return
      const map = Object.fromEntries(results)
      setDocs(prev => prev.map(d => (d.id in map ? { ...d, _nextSlot: map[d.id] || null } : d)))
    })()
    return () => { cancelled = true }
  }, [docs])

  const filtered = useMemo(() => {
    // ★★ P1 round 4 (L2): My Doctors is a DIRECTORY, not a picker, so it hides nobody for
    //   hours. Round 3 filtered it with hasRealHours, and a seen doctor fetched by id
    //   (no `bookable` key then, no next_available_date) vanished from the patient's own
    //   list. Only a picker that leads to a send hides a physician without real hours
    //   (utils/shareDoctorPanel hasRealHours); from here the profile says "online booking
    //   unavailable" instead of "Start a case" (DoctorProfileScreen), on the `bookable`
    //   that GET /doctors/<id>/full-profile now carries.
    const listed = docs
    if (filter === 'favorites') return listed.filter(d => d._isFavorite)
    if (filter === 'seen') return listed.filter(d => d._isSeen)
    if (filter === 'innetwork') return listed.filter(d => isDoctorInNetwork(d, insurance))
    return listed
  }, [docs, filter, insurance])

  const groups = useMemo(() => {
    // s123 (board D): "All" is grouped by how the patient knows the doctor — Seen through Orella, then Favorites.
    if (filter === 'all') {
      const seen = filtered.filter((d) => d._isSeen)
      const favs = filtered.filter((d) => d._isFavorite && !d._isSeen)
      return [
        ...(seen.length ? [[t('doctors.seenThroughOrella', { defaultValue: 'Seen through Orella' }), seen]] : []),
        ...(favs.length ? [[t('doctors.favorites', { defaultValue: 'Favorites' }), favs]] : []),
      ]
    }
    const g = {}
    filtered.forEach(d => {
      const spec = (d.specialties?.[0] || d.specialty || t('doctors.otherSpecialty', { defaultValue: 'Other' }))
      ;(g[spec] = g[spec] || []).push(d)
    })
    Object.values(g).forEach(list => list.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '')))
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]))
  }, [filtered, filter, t])

  const chips = [
    ['all', t('doctors.all', { defaultValue: 'All' })],
    ['favorites', t('doctors.favorites', { defaultValue: 'Favorites' })],
    ['seen', t('doctors.seenThroughOrella', { defaultValue: 'Seen through Orella' })],
    ['innetwork', t('doctors.inNetwork', { defaultValue: 'In-network' })],
  ]

  return (
    <div className="min-h-full bg-white">
      {/* s123 (board D): the Doctors & Clinicians navy header, with what this screen holds. */}
      <div className="px-4 pb-6 flex items-center gap-3 mb-4" style={{ background: '#15354F', borderBottomLeftRadius: 24, borderBottomRightRadius: 24, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
        <button onClick={() => navigate(-1)} aria-label={t('common.back', { defaultValue: 'Back' })} className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}><ArrowLeft size={20} /></button>
        <div className="min-w-0">
          <h1 className="text-[22px] font-extrabold" style={{ color: '#fff' }}>{t('doctors.myDoctors', { defaultValue: 'My Doctors' })}</h1>
          <p className="text-[12.5px]" style={{ color: '#A7D9FC' }}>{t('doctors.myDoctorsSub', { defaultValue: 'Your primary care, past Orella visits and favorites' })}</p>
        </div>
      </div>
      <div className="px-4 pb-8">
        {/* s123 (board D): Primary care first — the PCP saved in Profile › Insurance, on Orella or not (his ruling). */}
        {filter === 'all' && pcp && (
          <div className="mb-5" data-testid="my-doctors-pcp">
            <div className="flex items-baseline justify-between px-0.5">
              <p className="text-[12px] font-extrabold uppercase tracking-wider" style={{ color: '#475569' }}>{t('doctors.primaryCare', { defaultValue: 'Primary care' })}</p>
              <button onClick={() => navigate('/profile/insurance')} className="text-[12px] font-bold" style={{ color: '#46A8F9' }}>{t('doctors.editPcpInProfile', { defaultValue: 'Edit in Profile › Insurance' })}</button>
            </div>
            <div className="-mt-3"><PcpRow pcp={pcp} heading={false} onSend={setStartOffice} /></div>
          </div>
        )}
        <div className="flex gap-2 mb-4 flex-wrap">
          {chips.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors"
              style={filter === key ? { background: '#15354F', color: '#fff' } : { background: '#fff', border: '1px solid #E7EEF4', color: '#15354F' }}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <OrellaSpinner size={32} className="mb-3" />
            <p className="text-sm" style={{ color: '#64748B' }}>{t('doctors.findingDoctors')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Users size={30} style={{ color: '#9FB3C4' }} className="mb-3" />
            <p className="text-sm font-semibold" style={{ color: '#15354F' }}>{t('doctors.myDoctorsEmpty', { defaultValue: 'No doctors here yet' })}</p>
            <p className="text-xs mt-1" style={{ color: '#64748B' }}>{t('doctors.myDoctorsEmptySub', { defaultValue: 'Doctors you favorite or visit will appear here.' })}</p>
          </div>
        ) : (
          groups.map(([spec, list]) => (
            <div key={spec} className="mb-5">
              <p className="text-[11px] font-extrabold uppercase tracking-wide mb-2.5" style={{ color: '#46A8F9' }}>{spec}</p>
              <div className="space-y-2">
                {list.map(d => (
                  <div key={d.id}>
                    <DoctorCard doc={d} isMyDoctor={d._isSeen} isFavorite={d._isFavorite} insurance={insurance} onTap={() => navigate(`/doctor/${d.id}`, { state: { mode: 'browse' } })} />
                    {d._isSeen && d._lastCase ? (
                      <p className="text-[11.5px] mt-1 px-1" style={{ color: '#64748B' }}>{t('doctors.lastCase', { defaultValue: 'Last case {{date}}', date: formatDate(d._lastCase) })}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
        {!loading && filtered.some((d) => d._isFavorite) ? null : !loading && filtered.length > 0 ? (
          <p className="text-[12px] mt-1 px-1" style={{ color: '#64748B' }}>{t('doctors.favoritesHint', { defaultValue: 'Tap the heart on any clinician to keep them here.' })}</p>
        ) : null}
      </div>
      {startOffice && <StartCaseSheet office={startOffice} onClose={() => setStartOffice(null)} />}
    </div>
  )
}
